import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import MUIDataTable from 'mui-datatables';
import {
  Typography,
  Button,
  TextField,
  Grid,
  Icon,
  FormControl,
  FormHelperText,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  getGlobalLibraryAPI,
  downloadGlobalLibraryTemplateAPI,
  getMetadataAPI
} from 'api';
import { Breadcrumb, BackdropCircular, PromoDetailsModal } from 'components';
import MarkdownIt from 'markdown-it';
import noImage from '../../components/Logo/no-image.png';
// import SearchField from './SearchField';


const md = new MarkdownIt();


const useStyles = makeStyles({
  cell_header: {
    '& button span': {
      fontWeight: 'bold',
      fontSize: '14px'
    },
  },
  cell_header_centered: {
    '& button span': {
      display: 'flex',
      textAlign: 'center'
    },
    '& button': {
      width: '100%'
    },
  },
  download_button: {
    cursor: 'pointer',
  },
  download_button_disable: {
    cursor: 'not-allowed',
  },
  custom_input: {
    '& > div': {
      borderRadius: '10px',
      backgroundColor: '#fff'
    }
  }
});


const BrandPromotions = () => {
  const {id: selectedLabelId, tab} = useParams();
  const dispatch = useDispatch();
  const classes = useStyles();

  const [selectedPromo, setSelectedPromo] = useState({});

  const {
    globalLibraryData = [],
    totalCount,
    templateDownloading,
    libraryLoading
  } = useSelector((state) => state.library);
  const breadcrumbActiveTabName = tab.replace(/-/g, ' ');
  const { stores = [] } = useSelector((state) => state.user.profile);
  const { browsePromoLoading, metadata } = useSelector((state) => state.promo);

  const [searchParams, setSearchParams] = useState({ page: 1, limit: 10, name: '', labelsIds: [selectedLabelId],  });
  const loading = templateDownloading || libraryLoading || browsePromoLoading;
  const promoMetaDataLabel = Object.values(metadata).reduce((acc, val) => {
    const metaData = val.find((v) => v._id == selectedLabelId);

    if (metaData) {
      acc = metaData.label;
    }

    return acc;
  }, '');

  useEffect(() => {
    getMetadataAPI({dispatch, usedIn: 'globalLibrary'});
  }, []);

  useEffect(() => {
    dispatch(getGlobalLibraryAPI({ ...searchParams }));
  }, [searchParams]);

  const columns = [
    {
      name: 'banner',
      label: 'Banner',
      options: {
        sort: true,
        filter: false,
        setCellHeaderProps: () => ({className: `${classes.cell_header} ${classes.cell_header_centered}`}),
        customBodyRenderLite: (dataIndex) => {
          const promo = globalLibraryData[dataIndex];
          const {slider, promoName} = promo;

          return (
            <div className="flex items-center max-w-250 w-full">
              <Button
                size="small"
                onClick={() => setSelectedPromo(globalLibraryData[dataIndex])}
              >
                <img
                  src={slider.bannerImageInfo?.imageS3 || noImage}
                  alt={promoName}
                  className="w-full cursor-pointer"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = noImage;
                  }}
                />
              </Button>
            </div>
          );
        }
      },
    },
    {
      name: 'promoName',
      label: 'Promotion Title',
      options: {
        sort: true,
        filter: true,
        setCellHeaderProps: () => ({className: classes.cell_header}),
        customBodyRenderLite: (dataIndex) => {
          const {promoName} = globalLibraryData[dataIndex];
          return (
            <div className="text-16 text-brand">
              {promoName}
            </div>
          );
        }
      },
    },
    {
      name: '',
      label: 'Promotion Details',
      options: {
        sort: false,
        filter: false,
        setCellHeaderProps: () => ({className: 'text-14 font-bold'}),
        customBodyRenderLite: (dataIndex) => {
          const {promoDescription, promoType} = globalLibraryData[dataIndex];
          return (
            <>
              <Typography variant="body2" color="textSecondary" component="p" className="mr-4">
                <span className="font-semibold">Discount Type: </span>
                {promoType?.typeName ? promoType?.typeName : '-'}
              </Typography>
              <Typography variant="body2" color="textSecondary" component="div" className={classes.description} title={promoDescription}>
                <span className="font-semibold">Description: </span>
                {promoDescription ? <div dangerouslySetInnerHTML={{ __html: md.render(promoDescription || '') }} /> : '-'}
              </Typography>
            </>
          );
        }
      }
    },
    {
      name: '',
      label: '',
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({ style: { width: '10%'}, className: 'text-14'}),
        customBodyRenderLite: (dataIndex) => {
          const template = globalLibraryData[dataIndex];
          const alreadyDownloaded = template.storesDownloaded.includes(stores[0]?.apiUrl);
          return (
            <div
              title={alreadyDownloaded
                ? 'This Promotion is already downloaded'
                : 'Download this Promotion'
              }
              className={alreadyDownloaded
                ? classes.download_button_disable
                : classes.download_button
              }
            >
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleDownloadTemplate(template._id)}
                disabled={alreadyDownloaded}
              >
                <Icon>cloud_download</Icon>
              </Button>
            </div>
          );
        }
      },
    }
  ];

  const handleDownloadTemplate = (templateId) => {
    downloadGlobalLibraryTemplateAPI({dispatch, templateId});
  };

  return (
    <div className="flex-grow h-full min-h-full-screen scroll-y">
      <div className="mb-20">
        <Breadcrumb
          routeSegments={[
            { name: 'Promotions' },
            { name: 'Automatic Promotions', path: '/my-promotions'},
            { name: `${breadcrumbActiveTabName}`, path: `/promotions-library/${tab}`},
            { name: `${promoMetaDataLabel} Promotions`}
          ]}
        />
        <Typography variant="h2" className="mt-10 ml-10 text-brand text-44">
          {promoMetaDataLabel}{' '}Promotions
        </Typography>
        <Grid container className="px-10 mt-10">
          <Grid item xs={12} sm={8} md={6}>
            <FormControl  className="w-full pb-4 pr-14 flex items-center relative">
              <TextField
                fullWidth
                variant="outlined"
                label="Search promotion by name"
                className={`${classes.custom_input}`}
                onKeyPress={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    setSearchParams((searchParams) => ({...searchParams, name: event.target.value}));
                  }
                }}
              />
              {searchParams.name.length === 1 &&
                <FormHelperText
                  className="position-bottom"
                  style={{left: '10px', bottom: '-10px'}}
                >
                  Enter two or more symbols and press Enter or Search icon
                </FormHelperText>
              }
              {!!searchParams.name.length &&
              <>
                <Icon
                  className="text-muted absolute cursor-pointer"
                  fontSize="small"
                  style={{right: '65px', top: '11px'}}
                  onClick={() => {
                    setSearchParams((searchParams) => ({...searchParams, name: ''}));
                  }}
                >
                  close
                </Icon>
              </>
              }
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={3} className="px-10 mt-4">
          {/* {Object.entries(metadata).map(([key, val]) => {
            return (
              <SearchField
                key={key}
                selectedLabelId={selectedLabelId}
                options={val}
                name={key}
                handleChangeFilter={setSearchParams}
              />);
          })} */}
        </Grid>
        <div className="m-10 border-radius-12 elevation-z25 border-1-light-grey">
          {!globalLibraryData.length ?
            <Typography variant="h5" className="text-muted p-8">No matching items found</Typography>
            :
            <MUIDataTable
              data={globalLibraryData}
              columns={columns}
              options={{
                selectableRows: 'none',
                search: false,
                filter: false,
                download: false,
                print: false,
                pagination: true,
                viewColumns: false,
                rowsPerPage: searchParams.limit,
                page: searchParams.page - 1,
                rowsPerPageOptions: [10, 20, 40, 80, 100],
                serverSide: true,
                count: totalCount,
                onChangePage: (currentPage) => setSearchParams((f) => ({...f, page: currentPage + 1})),
                onChangeRowsPerPage: (newLimit) => setSearchParams((f) => ({...f, limit: newLimit, page: 1})),
                setTableProps: () => ({className: 'border-radius-12'})
              }}
            />
          }
        </div>
      </div>
      <PromoDetailsModal selectedPromo={selectedPromo} setSelectedPromo={setSelectedPromo} />
      <BackdropCircular loading={loading} />
    </div>
  );
};

export default BrandPromotions;
